import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./home-videos.scss";
import { Spinner } from "react-bootstrap";
import { URL } from "../hooks/fetchdata";

// Icons
import 'boxicons/css/boxicons.min.css';

const HomeVideos = () => {
  const [allCourses, setAllCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("accessToken");

  const getCourses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${URL}/api/courses/`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Ma'lumotlarni yuklashda xatolik yuz berdi");
      }

      const data = await response.json();
      
      setAllCourses(data);
      setCourses(data);
      
      // Agar kurslarda category bo'lmasa, sun'iy kategoriyalar yaratamiz
      // yoki boshqa ma'lumotlardan kategoriya olamiz
      const extractedCategories = extractCategories(data);
      setCategories(extractedCategories);
      
    } catch (error) {
      console.error("Xatolik:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Kategoriyalarni ajratib olish funksiyasi
  const extractCategories = (courses) => {
    // Agar kurslarda category bo'lmasa, default kategoriyalar
    // yoki kurslardan olingan boshqa ma'lumotlar asosida kategoriya yaratish
    
    // Misol uchun, channel_id bo'yicha kategoriyalar yaratish
    const categoryMap = new Map();
    
    courses.forEach(course => {
      // Agar course.category mavjud bo'lsa
      if (course.category) {
        categoryMap.set(course.category.id, course.category);
      } else {
        // Agar category bo'lmasa, channel_id asosida kategoriya yaratamiz
        const categoryId = course.channel_id || 'default';
        const categoryTitle = course.channel_name || 'Boshqa kurslar';
        
        categoryMap.set(categoryId, {
          id: categoryId,
          title: categoryTitle,
          count: (categoryMap.get(categoryId)?.count || 0) + 1
        });
      }
    });
    
    return Array.from(categoryMap.values());
  };

  useEffect(() => {
    getCourses();
  }, []);

  // Kategoriya bo‘yicha filter
  const handleCourseById = (categoryId) => {
    if (activeCategory === categoryId) {
      // Agar bir xil kategoriya bosilsa, filtrni tozalaymiz
      setActiveCategory(null);
      setCourses(sortCourses(allCourses, sortOrder));
    } else {
      setActiveCategory(categoryId);
      
      const filtered = allCourses.filter((course) => {
        // Agar course.category mavjud bo'lsa
        if (course.category) {
          return course.category.id === categoryId;
        }
        // Agar category bo'lmasa, channel_id bo'yicha filter
        return course.channel_id === categoryId;
      });
      
      setCourses(sortCourses(filtered, sortOrder));
    }
  };

  // Sort funksiyasi
  const sortCourses = (data, order) => {
    const sorted = [...data];

    switch (order) {
      case "newest":
        return sorted.sort(
          (a, b) => new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at)
        );
      case "oldest":
        return sorted.sort(
          (a, b) => new Date(a.published_at || a.created_at) - new Date(b.published_at || b.created_at)
        );
      case "videos":
        return sorted.sort(
          (a, b) => (b.video_count || 0) - (a.video_count || 0)
        );
      case "title":
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      default:
        return sorted;
    }
  };

  // Sort o'zgarganda filtrlangan ma'lumotlarni qayta sort qilish
  useEffect(() => {
    if (allCourses.length === 0) return;

    const baseData = activeCategory
      ? allCourses.filter((course) => {
          if (course.category) {
            return course.category.id === activeCategory;
          }
          return course.channel_id === activeCategory;
        })
      : allCourses;

    setCourses(sortCourses(baseData, sortOrder));
  }, [sortOrder, activeCategory, allCourses]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Kurslar yuklanmoqda...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <i className='bx bx-error-circle'></i>
        <p>{error}</p>
        <button onClick={getCourses} className="retry-btn">
          Qayta urinish
        </button>
      </div>
    );
  }

  return (
    <div className="videos-page">
      <div className="container">
        <div className="videos-wrapper">
          {/* Sidebar */}
          <aside className="sidebar">
            <div className="sidebar-header">
              <h5>Bo'limlar</h5>
              {activeCategory && (
                <button 
                  className="clear-filter"
                  onClick={() => handleCourseById(activeCategory)}
                >
                  <i className='bx bx-x'></i>
                  Tozalash
                </button>
              )}
            </div>

            {categories.length > 0 ? (
              <ul className="category-list">
                {categories.map((item) => (
                  <li key={item.id}>
                    <label className={`category-item ${activeCategory === item.id ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={activeCategory === item.id}
                        onChange={() => handleCourseById(item.id)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="category-title">{item.title}</span>
                      <span className="category-count">
                        {allCourses.filter(c => 
                          c.category ? c.category.id === item.id : c.channel_id === item.id
                        ).length}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-categories">
                Kategoriyalar mavjud emas
              </p>
            )}
          </aside>

          {/* Main Content */}
          <main className="main-content">
            {/* Top bar */}
            <div className="content-header">
              <div className="results-info">
                <h5>Kurslar</h5>
                <span className="results-count">
                  {courses.length} ta kurs topildi
                </span>
              </div>
              
              <div className="sort-wrapper">
                <label htmlFor="sort-select">Saralash:</label>
                <select
                  id="sort-select"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="sort-select"
                >
                  <option value="">Barchasi</option>
                  <option value="newest">Eng yangi</option>
                  <option value="oldest">Eski</option>
                  <option value="videos">Video soni</option>
                  <option value="title">Nomi (A-Z)</option>
                </select>
              </div>
            </div>

            {/* Courses Grid */}
            {courses.length > 0 ? (
              <div className="courses-grid">
                {courses.map((course) => (
                  <CourseCard key={course.guid || course.id} course={course} />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <i className='bx bx-folder-open'></i>
                <p>Hech qanday kurs topilmadi</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

// Course Card Component
const CourseCard = ({ course }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Link to={`/videos/${course.guid}`} className="course-card">
      <div className="card-image">
        {!imageError ? (
          <img 
            src={course.thumbnail_url} 
            alt={course.title}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="image-placeholder">
            <i className='bx bx-video'></i>
          </div>
        )}
        
        {course.video_count > 0 && (
          <span className="video-count">
            <i className='bx bx-play-circle'></i>
            {course.video_count}
          </span>
        )}
      </div>
      
      <div className="card-content">
        <h3 className="course-title">{course.title}</h3>
        
        <div className="course-meta">
          <span className="channel-name">
            <i className='bx bx-video'></i>
            {course.video_count}
          </span>
          
          <span className="publish-date">
            <i className='bx bx-calendar'></i>
            {new Date(course.published_at || course.created_at).toLocaleDateString('uz-UZ')}
          </span>
        </div>
        
        {course.description && (
          <p className="course-description">
            {course.description.length > 100 
              ? `${course.description.slice(0, 100)}...` 
              : course.description}
          </p>
        )}
      </div>
    </Link>
  );
};

export default HomeVideos;